// Reine Logik-Helfer fuer die Artikel-Analyse-Seite (Task 16, pages/analytics/ArticleAnalysis.tsx).
// Bewusst aus der Komponente ausgelagert, weil das die einzigen nicht-trivialen, ohne
// React testbaren Teile der Seite sind: Debounce-Schwelle der GA-Suche, Metrik-Umbau der
// Artikel-Zeitreihe fuer StackedBarChart, und die Breadcrumb-Pfad-Arithmetik des
// Kategorien-Drilldowns (Off-by-one ist hier der typische Fehler).
import type { ArticleSeriesBucket, DashSeriesBucket, TopRow } from "@/api/dashboard";

// Mindestlaenge, ab der die GA-Suche ueberhaupt einen Request ausloest (zusaetzlich zum
// 300ms-Debounce in der Komponente). Verhindert Server-Anfragen fuer 1-Zeichen-Eingaben,
// die ohnehin nur Rauschen liefern wuerden — analog zum bestehenden Server-Suche-Debounce
// in pages/stock/StockList.tsx (300ms), hier zusaetzlich mit einer Laengen-Schwelle, weil
// die GA-Suche (anders als StockList) serverseitig ueber Sternnummer/SKU/Name gegen ~1.800
// Grundartikel matcht.
export const MIN_SEARCH_LENGTH = 2;

export function isSearchable(term: string): boolean {
  return term.trim().length >= MIN_SEARCH_LENGTH;
}

// Wandelt die verschachtelte Artikel-Zeitreihe ({qty,revenue} je Kanal und Zeit-Bucket)
// in das flache Record<Kanal, number>-Format um, das StackedBarChart (Task 14) erwartet —
// je nach gewaehlter Metrik (Menge oder Umsatz). Kanaele ohne Eintrag in einem Bucket
// tauchen im Ergebnis-Bucket schlicht nicht auf (StackedBarChart behandelt fehlende
// Kanaele als 0, siehe stackedBarChartLayout).
export function toArticleStackedBuckets(
  buckets: ArticleSeriesBucket[],
  metric: "qty" | "revenue",
): DashSeriesBucket[] {
  return buckets.map((bucket) => {
    const values: Record<string, number> = {};
    for (const [channel, v] of Object.entries(bucket.values)) {
      values[channel] = v[metric];
    }
    return { label: bucket.label, values };
  });
}

// Kategorie-Breadcrumb-Pfad (Wurzel = leeres Array = "Alle Kategorien"). Haengt die
// angeklickte Zeile nur an, wenn sie eine gueltige Kategorie-Id hat — id===null ist ein
// Datenfehler des category-dim (buildTopSql liefert fuer dim='category' immer f.id), ein
// Drilldown ohne Id waere ein kaputter Pfad ohne Filter-Wirkung.
export function pushCategory(path: TopRow[], row: TopRow): TopRow[] {
  if (row.id === null) return path;
  return [...path, row];
}

// Springt im Breadcrumb auf eine hoehere Ebene zurueck. index=-1 (Klick auf "Alle
// Kategorien") leert den Pfad komplett. index >= path.length-1 ist ein No-Op auf der
// aktuellen Ebene (slice deckelt implizit).
export function popCategoryTo(path: TopRow[], index: number): TopRow[] {
  if (index < 0) return [];
  return path.slice(0, index + 1);
}

// Die aktuell aktive Eltern-Kategorie-Id fuer den naechsten useTop('category', ...)-Call
// — null an der Wurzel (kein `parent`-Query-Param, Backend liefert Top-Level-Ordner).
export function currentCategoryParent(path: TopRow[]): number | null {
  if (path.length === 0) return null;
  return path[path.length - 1]!.id;
}
